import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import type {
    AbsenceTypeValue,
    StaffAbsenceDto,
    StaffAbsencePayload,
    AbsenceApptConflictDto,
    AbsencePolicyBreachDto,
} from '../../types/api-responses';
import { resolveWorkingWindow, isOutsideWorkingHours, findOverlappingAbsence, describeAbsence } from '../../lib/staffScheduleHint';
import AbsenceConflictConfirm from '../StaffAbsence/AbsenceConflictConfirm';
import styles from './StaffAbsencesSection.module.css';

/**
 * AB#1976 (CR-2026-07-04) — Owner-facing staff absence management.
 * Self-contained: fetches / creates / deletes absences for one staff member
 * via TanStack Query. Rendered inside ManageStaffModal in edit mode.
 *
 * Intra-day types (Break, Emergency) carry a start/end time; whole-day leave
 * (Sick, Vacation, Personal) uses a date range only. The BE echoes any
 * non-blocking warning (e.g. all-staff-unavailable) which we surface as a
 * toast + inline banner.
 */
export interface StaffAbsencesSectionProps {
    staffId: number;
    /** Multi-branch businesses must scope each absence to a branch. */
    branches: { id: number; name: string }[];
}

const INTRA_DAY: AbsenceTypeValue[] = [0, 3, 4]; // Break, Personal time, Emergency (time window)
const REASON_TYPES: AbsenceTypeValue[] = [0, 4]; // Reason only meaningful for Break / Emergency

const TYPE_OPTIONS: { value: AbsenceTypeValue; key: string }[] = [
    { value: 0, key: 'staff.absence.type.break' },
    { value: 1, key: 'staff.absence.type.sick' },
    { value: 2, key: 'staff.absence.type.vacation' },
    { value: 3, key: 'staff.absence.type.personal' },
    { value: 4, key: 'staff.absence.type.emergency' },
];

const REASON_OPTIONS = [
    { value: 'Lunch', key: 'staff.absence.reason.lunch' },
    { value: 'Prayer', key: 'staff.absence.reason.prayer' },
    { value: 'Rest', key: 'staff.absence.reason.rest' },
    { value: 'Medical', key: 'staff.absence.reason.medical' },
    { value: 'Other', key: 'staff.absence.reason.other' },
];

const absenceTypeKey: Record<string, string> = {
    Break: 'staff.absence.type.break',
    SickLeave: 'staff.absence.type.sick',
    Vacation: 'staff.absence.type.vacation',
    PersonalTime: 'staff.absence.type.personal',
    Emergency: 'staff.absence.type.emergency',
};

const StaffAbsencesSection: React.FC<StaffAbsencesSectionProps> = ({ staffId, branches }) => {
    const { t, language } = useLanguage();
    const toast = useToast();
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => ['staff-absences', staffId] as const, [staffId]);

    const isMultiBranch = branches.length >= 2;
    const [showForm, setShowForm] = useState(false);
    // AB#2016 — appointment-conflict confirm (non-null → dialog shown).
    const [conflicts, setConflicts] = useState<AbsenceApptConflictDto[] | null>(null);
    const [policyBreach, setPolicyBreach] = useState<AbsencePolicyBreachDto | null>(null);

    const today = new Date().toISOString().slice(0, 10);
    const [type, setType] = useState<AbsenceTypeValue>(0);
    const [branchId, setBranchId] = useState<number | null>(branches[0]?.id ?? null);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [startTime, setStartTime] = useState('13:00');
    const [endTime, setEndTime] = useState('14:00');
    const [reason, setReason] = useState('Lunch');
    const [isRecurring, setIsRecurring] = useState(true);

    const isIntraDay = INTRA_DAY.includes(type);
    const showReason = REASON_TYPES.includes(type);

    const { data, isLoading } = useQuery({
        queryKey,
        queryFn: () => apiService.getStaffAbsences(staffId),
    });

    // AB#2011 — per-branch schedule for the working-hours hint + soft warning.
    const { data: schedule } = useQuery({
        queryKey: ['staff-schedule', staffId] as const,
        queryFn: () => apiService.getStaffSchedule(staffId),
        staleTime: 60_000,
    });
    const workWindow = useMemo(
        () => resolveWorkingWindow(schedule?.slots, branchId, startDate),
        [schedule, branchId, startDate],
    );
    const outOfHours = isIntraDay && isOutsideWorkingHours(workWindow, startTime, endTime);

    const createMut = useMutation({
        mutationFn: (payload: StaffAbsencePayload) => apiService.createStaffAbsence(staffId, payload),
        onSuccess: (res) => {
            // AB#2016 + AB#2024 — needs owner confirmation (orphaned bookings
            // and/or break exceeds branch caps); nothing saved.
            if (res.requiresConfirmation) {
                setConflicts(res.conflictingAppointments ?? []);
                setPolicyBreach(res.policyBreach ?? null);
                return;
            }
            setConflicts(null);
            setPolicyBreach(null);
            queryClient.invalidateQueries({ queryKey });
            setShowForm(false);
            toast.showToast(t('staff.absence.toast.saved'), 'success');
            for (const w of res.warnings ?? []) {
                toast.showToast(language === 'ar' ? w.messageAr : w.message, 'warning');
            }
        },
        onError: (err: unknown) => {
            // AB#2012 — surface the server block reason (localized).
            const raw = JSON.stringify(
                (err as { response?: { data?: unknown } })?.response?.data ?? '');
            const key = raw.includes('Overlap') ? 'staff.absence.error.overlap'
                : (raw.includes('working hours') || raw.includes('not scheduled'))
                    ? 'staff.absence.error.outsideHours'
                    : 'staff.absence.toast.saveFailed';
            toast.showToast(t(key), 'error');
        },
    });

    const deleteMut = useMutation({
        mutationFn: (absenceId: number) => apiService.deleteStaffAbsence(staffId, absenceId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
            toast.showToast(t('staff.absence.toast.deleted'), 'success');
        },
        onError: () => toast.showToast(t('staff.absence.toast.deleteFailed'), 'error'),
    });

    const submit = () => {
        if (isMultiBranch && branchId == null) {
            toast.showToast(t('staff.absence.error.branchRequired'), 'error');
            return;
        }
        // Intra-day types have a single Date field (endDate is derived from
        // startDate in the payload below), so the range check must not run —
        // otherwise moving the date forward trips a false "end < start" error.
        if (!isIntraDay && endDate < startDate) {
            toast.showToast(t('staff.absence.error.dateOrder'), 'error');
            return;
        }
        if (isIntraDay && endTime <= startTime) {
            toast.showToast(t('staff.absence.error.timeOrder'), 'error');
            return;
        }
        // AB#2012 — hard block (mirrors the server): out-of-hours/off-day for
        // intra-day types, and overlapping/duplicate absences.
        if (outOfHours) {
            toast.showToast(t('staff.absence.error.outsideHours'), 'error');
            return;
        }
        const effEndDate = isIntraDay ? startDate : endDate;
        const conflict = findOverlappingAbsence(
            data?.absences, null, startDate, effEndDate,
            isIntraDay ? `${startTime}` : null, isIntraDay ? `${endTime}` : null);
        if (conflict) {
            // AB#2018 — whole-day-vs-whole-day reads as "already on leave"; an
            // intra-day clash is a duplicate window.
            const key = isIntraDay ? 'staff.absence.error.overlapDetail' : 'staff.absence.error.leaveOverlap';
            toast.showToast(
                t(key).replace('{details}', describeAbsence(conflict, t)),
                'error');
            return;
        }
        createMut.mutate(buildPayload(false));
    };

    const buildPayload = (force: boolean): StaffAbsencePayload => ({
        absenceType: type,
        branchId,
        startDate,
        endDate: isIntraDay ? startDate : endDate,
        startTime: isIntraDay ? `${startTime}:00` : null,
        endTime: isIntraDay ? `${endTime}:00` : null,
        reason: showReason ? reason : null,
        isRecurring: type === 0 ? isRecurring : false,
        recurrencePattern: type === 0 && isRecurring ? 'Weekdays' : null,
        force,
    });

    const absences = data?.absences ?? [];

    const formatRange = (a: StaffAbsenceDto): string => {
        if (a.startTime && a.endTime) {
            const rec = a.isRecurring ? ` · ${t('staff.absence.recurring')}` : '';
            return `${a.startTime}–${a.endTime}${rec}`;
        }
        return a.startDate === a.endDate ? a.startDate : `${a.startDate} → ${a.endDate}`;
    };

    return (
        <div className={styles.section}>
            <div className={styles.header}>
                <span className={styles.title}>{t('staff.absence.title')}</span>
                {!showForm && (
                    <button type="button" className={styles.addBtn} onClick={() => setShowForm(true)}>
                        + {t('staff.absence.add')}
                    </button>
                )}
            </div>

            {isLoading ? (
                <p className={styles.muted}>{t('staff.absence.loading')}</p>
            ) : absences.length === 0 && !showForm ? (
                <p className={styles.empty}>{t('staff.absence.empty')}</p>
            ) : (
                <ul className={styles.list}>
                    {absences.map((a) => (
                        <li key={a.id} className={`${styles.item} ${styles['t_' + a.absenceType] ?? ''}`}>
                            <div className={styles.itemBody}>
                                <span className={styles.itemType}>
                                    {t(absenceTypeKey[a.absenceType] ?? 'staff.absence.type.break')}
                                </span>
                                <span className={styles.itemRange}>{formatRange(a)}</span>
                                {a.reason && <span className={styles.itemReason}>{a.reason}</span>}
                            </div>
                            <button
                                type="button"
                                className={styles.removeBtn}
                                disabled={deleteMut.isPending}
                                onClick={() => deleteMut.mutate(a.id)}
                            >
                                {t('staff.absence.remove')}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {showForm && (
                <div className={styles.form}>
                    <div className={styles.row}>
                        <label className={styles.field}>
                            <span>{t('staff.absence.field.type')}</span>
                            <select value={type} onChange={(e) => setType(Number(e.target.value) as AbsenceTypeValue)}>
                                {TYPE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{t(o.key)}</option>
                                ))}
                            </select>
                        </label>
                        {isMultiBranch && (
                            <label className={styles.field}>
                                <span>{t('staff.absence.field.branch')}</span>
                                <select
                                    value={branchId ?? ''}
                                    onChange={(e) => setBranchId(e.target.value ? Number(e.target.value) : null)}
                                >
                                    {branches.map((b) => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>

                    {isIntraDay ? (
                        <div className={styles.row}>
                            <label className={styles.field}>
                                <span>{t('staff.absence.field.date')}</span>
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            </label>
                            <label className={styles.field}>
                                <span>{t('staff.absence.field.startTime')}</span>
                                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                            </label>
                            <label className={styles.field}>
                                <span>{t('staff.absence.field.endTime')}</span>
                                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                            </label>
                        </div>
                    ) : (
                        <div className={styles.row}>
                            <label className={styles.field}>
                                <span>{t('staff.absence.field.startDate')}</span>
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            </label>
                            <label className={styles.field}>
                                <span>{t('staff.absence.field.endDate')}</span>
                                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                            </label>
                        </div>
                    )}

                    {isIntraDay && workWindow.kind !== 'unknown' && (
                        <div className={styles.hoursHint}>
                            {workWindow.kind === 'working'
                                ? t('staff.absence.hours.works').replace(
                                    '{range}', `⁨${workWindow.start}–${workWindow.end}⁩`)
                                : t('staff.absence.hours.off')}
                        </div>
                    )}
                    {outOfHours && (
                        <div className={styles.hoursWarn}>⚠ {t('staff.absence.hours.warnOutside')}</div>
                    )}

                    {showReason && (
                        <div className={styles.row}>
                            <label className={styles.field}>
                                <span>{t('staff.absence.field.reason')}</span>
                                <select value={reason} onChange={(e) => setReason(e.target.value)}>
                                    {REASON_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{t(o.key)}</option>
                                    ))}
                                </select>
                            </label>
                            {type === 0 && (
                                <label className={`${styles.field} ${styles.checkField}`}>
                                    <input
                                        type="checkbox"
                                        checked={isRecurring}
                                        onChange={(e) => setIsRecurring(e.target.checked)}
                                    />
                                    <span>{t('staff.absence.field.recurring')}</span>
                                </label>
                            )}
                        </div>
                    )}

                    <div className={styles.formActions}>
                        <button
                            type="button"
                            className={styles.saveBtn}
                            disabled={createMut.isPending}
                            onClick={submit}
                        >
                            {t('staff.absence.save')}
                        </button>
                        <button type="button" className={styles.cancelBtn} onClick={() => setShowForm(false)}>
                            {t('staff.absence.cancel')}
                        </button>
                    </div>
                </div>
            )}

            {(conflicts || policyBreach) && (
                <AbsenceConflictConfirm
                    conflicts={conflicts ?? []}
                    policyBreach={policyBreach}
                    saving={createMut.isPending}
                    onConfirm={() => createMut.mutate(buildPayload(true))}
                    onCancel={() => { setConflicts(null); setPolicyBreach(null); }}
                />
            )}
        </div>
    );
};

export default StaffAbsencesSection;
